// Orca AI client state shared by the AI page and the global "Спросить Orca"
// bar: chat history (the bar's conversations land there too), message states
// (thinking → typing → done / error / stopped / limit), daily usage counter.
// ORCA API: every question goes to Orca AI on the server (the bot's ai_v15) and
// spends the account's one daily quota, shared with Telegram — only a real
// answer counts. Production history is account-scoped in PostgreSQL; the DEV
// mock keeps canned answers so every flow can be walked through.
import i18n from '@/i18n';
import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import { getMock, useMock } from '@/dev/mockState';
import { useAccount, useAccountAction, useOrcaAIData } from '@/hooks/queries';
import { accountApi, aiApi } from '@/lib/api/client';
import { MOCK_DATA } from '@/lib/api/mode';
import { HttpError } from '@/lib/api/http';
import { apiMessage } from '@/lib/api/real';
import { useQuery } from '@tanstack/react-query';
import { nowIso } from '@/lib/time';
import { useTime } from '@/hooks/useTime';
import type { Answer, Chat } from '@/types';
import type { AiContextDescriptor, AiConversation, AiConversationDetail } from '@/lib/api/real';
import { noPositionAnswer, type AskCtx } from './contexts';
import { useCaps } from '@/hooks/useCaps';
import { FEATURES, type Feature } from '@/lib/features';
import { isProductQuestion, lockedAnswer, productAnswer } from '@/features/plans/ask';
import { usePlanCatalog, usePlans, useTeasers } from '@/hooks/queries';
import { exampleQuestion, FEATURE_INFO, featureQuestion } from '@/features/plans/catalog';
import { price as fmtPrice } from '@/lib/format';
import { useQueryClient } from '@tanstack/react-query';
import type { Portfolio } from '@/types';
import { useLocation } from 'react-router';

export type ItemState = 'pending' | 'done' | 'error' | 'stopped' | 'limit' | 'unknown';
export type ItemId = number | string;
export type ThreadItem = {
  id: ItemId; q: string; answer: Answer; ctx?: AskCtx;
  state: ItemState;
  /** mock: this attempt fails (dev «AI error» fails the first attempt; «Повторить» succeeds) */
  fail: boolean;
  attempt: number;
  /** play thinking/typing (false for history and finished answers) */
  animate: boolean;
  /** a question about Orca's plans / features: answered even at the limit, costs nothing */
  free?: boolean;
  /** asked to Orca AI on the server (ORCA API): `sent` — the message as sent, `error` — why it failed */
  real?: boolean;
  sent?: string;
  error?: string;
  requestId?: string;
  messageId?: string;
};
export type ChatRec = { id: string; serverId?: string; title: string; tag: string; /** ISO */ at: string; items: ThreadItem[]; loaded?: boolean };

type OrcaCtx = {
  /** one daily quota for every question on the site (from the account) */
  used: number; limit: number; resetAt: string; limitReached: boolean;
  chats: ChatRec[];
  /** asks in a chat (creates one when `chatId` is empty); returns the chat id */
  ask: (q: string, o?: { chatId?: string | null; ctx?: AskCtx | null; answer?: Answer }) => string;
  finish: (chatId: string, itemId: ItemId, state: 'done' | 'error' | 'stopped') => void;
  retry: (chatId: string, itemId: ItemId) => void;
  removeChat: (chatId: string) => void;
  loadChat(chatId: string): Promise<void>;
  searchChats(query: string): Promise<ChatRec[]>;
  suggest: (base: string[], asked: string) => string[];
  /** an answer built on a feature the plan doesn't open → that feature's explanation (no content) */
  gate: (a: Answer) => Answer;
  /** a canned question whose answer the plan may get (suggestions never offer locked content) */
  allowed: (q: string) => boolean;
  bar: { open: boolean; chatId: string | null; ctx: AskCtx | null };
  /** «Спросить Orca» on any item (or a suggested question): opens the bar with its context and asks */
  askAbout: (ctx: AskCtx, question?: string) => void;
  /** what is on screen now (open preview > selected object > nothing): drives the bar's suggestions */
  screenCtx: AskCtx | null;
  registerCtx: (id: string, ctx: AskCtx | null, priority: number) => void;
  openBar: () => void;
  closeBar: () => void;
  askInBar: (q: string) => void;
  clearBarCtx: () => void;
  resetBar: () => void;
};

const Ctx = createContext<OrcaCtx | null>(null);
const SERVER_CHAT_ID = /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;
export const useOrca = () => {
  const c = useContext(Ctx);
  if (!c) throw new Error('useOrca outside OrcaProvider');
  return c;
};

const seed = (chats: Chat[], answers: Record<string, Answer>, fallback: Answer): ChatRec[] =>
  chats.map((c, k) => ({
    id: c.id, title: c.title, tag: c.tag, at: c.at,
    items: c.questions.map((q, i) => ({ id: -1000 * (k + 1) - i, q, answer: answers[q] ?? fallback, state: 'done', fail: false, attempt: 1, animate: false })),
  }));

const summaryChat = (c: AiConversation): ChatRec => ({
  id: c.id, serverId: c.id, title: c.title, tag: 'Orca AI', at: c.last_activity_at, items: [], loaded: false,
});
const iconFor = (kind: string) => ({ event: 'cal', alert: 'price', whale: 'whale', position: 'pie', post: 'x', video: 'yt', digest: 'home', ws: 'ws', coin: 'link', chart: 'price', news: 'news', page: 'home' }[kind] ?? 'ai');
const restoredContext = (c: AiContextDescriptor | null, question: string): AskCtx | undefined => c ? ({
  kind: c.kind as AskCtx['kind'], icon: iconFor(c.kind), label: c.label ?? c.tag ?? 'Orca AI', tag: c.tag ?? 'Orca AI',
  question, answer: { known: '', scenario: '', risk: '' }, suggestions: [], ...(c.reference_id ? { to: c.reference_id } : {}),
}) : undefined;
const detailChat = (c: AiConversationDetail): ChatRec => {
  const replies = new Map(c.messages.filter((m) => m.role === 'assistant' && m.generation_id).map((m) => [m.generation_id!, m]));
  const users = c.messages.filter((m) => m.role === 'user');
  const items: ThreadItem[] = users.map((m) => {
    const reply = m.generation_id ? replies.get(m.generation_id) : undefined;
    const ctx = restoredContext(m.context, m.content);
    return {
      id: m.id, messageId: m.id, requestId: m.generation_id ?? undefined, q: m.content, ctx,
      answer: { known: '', scenario: '', risk: '', ...(reply ? { html: reply.content } : {}) },
      state: reply ? 'done' : m.generation_status === 'pending' ? 'pending' : 'error',
      fail: false, attempt: 1, animate: false, real: true,
      sent: ctx ? `${ctx.label}\n${m.content}` : m.content,
    };
  });
  return { id: c.id, serverId: c.id, title: c.title, tag: users.at(-1)?.context?.tag ?? 'Orca AI', at: c.last_activity_at, items, loaded: true };
};
const contextDescriptor = (ctx?: AskCtx): AiContextDescriptor | undefined => ctx ? ({
  kind: ctx.kind, label: ctx.label, tag: ctx.tag, ...(ctx.to ? { reference_id: ctx.to } : {}),
}) : undefined;

export function OrcaProvider({ children }: { children: ReactNode }) {
  const { pathname } = useLocation();
  const { answers, fallback, chats: seedChats } = useOrcaAIData();
  const { plan, usage, coins } = useAccount();
  const tf = useTime();
  // ORCA API: the account's quota (Telegram + Web), read from the server and updated with each answer
  const quotaQ = useQuery({ queryKey: ['ai-quota'], queryFn: aiApi.quota, enabled: !MOCK_DATA, staleTime: 30_000, retry: 1 });
  const historyQ = useQuery({ queryKey: ['ai-conversations'], queryFn: () => aiApi.conversations(), enabled: !MOCK_DATA && pathname === '/ai', staleTime: 30_000, retry: 1 });
  // 00:00 UTC in the user's zone: «03:00 МСК (00:00 UTC)»
  const resetAt = !MOCK_DATA && quotaQ.data ? quotaQ.data.resets_at : usage.resetAt;
  const resetLabel = tf.tz === 'UTC' ? '00:00 UTC' : `${tf.time(resetAt)} ${tf.zone} (00:00 UTC)`;
  // the quota is the account's: /ai, the bar, «Спросить Orca» on any card share it
  // null: the shared quota counter (Redis) is not readable by the Web yet — Orca AI on the Web is
  // still the demo, its answers are counted locally until the AI phase connects the real quota
  const used = MOCK_DATA ? usage.aiToday ?? 0 : quotaQ.data?.used ?? 0;
  const spend = useAccountAction(accountApi.spendAi, (cur) => ({ ...cur, usage: { ...cur.usage, aiToday: Math.min(cur.plan.aiDailyLimit, (cur.usage.aiToday ?? 0) + 1) } }));
  const spendRef = useRef(spend.run);
  spendRef.current = spend.run;
  const [chats, setChats] = useState<ChatRec[]>(() => seed(seedChats, answers, fallback));
  const chatsRef = useRef(chats);
  chatsRef.current = chats;
  const [bar, setBarState] = useState<OrcaCtx['bar']>({ open: false, chatId: null, ctx: null });
  const barRef = useRef(bar);
  const setBar = useCallback((b: OrcaCtx['bar']) => { barRef.current = b; setBarState(b); }, []);
  const seq = useRef(0);
  const usedRef = useRef(used);
  usedRef.current = used;
  // answers in flight hold a place in the quota, so quick repeats can't overshoot it
  const inflight = useRef(new Set<ItemId>());
  /** product questions in flight: they settle like any answer but never touch the quota */
  const freeItems = useRef(new Set<ItemId>());
  // the server history changed (dev «empty», another language…) → reseed it; chats started
  // in this session are the user's own and stay
  const firstSeed = useRef(true);
  const seedIds = useRef(new Set(seedChats.map((c) => c.id)));
  useEffect(() => {
    if (firstSeed.current) { firstSeed.current = false; return; }
    const prev = seedIds.current;
    seedIds.current = new Set(seedChats.map((c) => c.id));
    setChats((cs) => [...cs.filter((c) => !prev.has(c.id)), ...seed(seedChats, answers, fallback)]);
  }, [seedChats]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => {
    if (MOCK_DATA || !historyQ.data) return;
    setChats((current) => {
      const matched = new Set<string>();
      const saved = historyQ.data.map((row) => {
        const old = current.find((c) => (c.serverId ?? c.id) === row.id);
        if (old) {
          matched.add(old.id);
          return { ...old, serverId: row.id, title: row.title, at: row.last_activity_at };
        }
        return summaryChat(row);
      });
      const local = current.filter((c) => !c.serverId && !matched.has(c.id));
      return [...local, ...saved];
    });
  }, [historyQ.data]);

  const mock = useMock();
  const limit = MOCK_DATA ? plan.aiDailyLimit : quotaQ.data?.limit ?? plan.aiDailyLimit;
  // the plan's capabilities: Orca never answers with a locked feature's data
  const caps = useCaps();
  // what locked features already found for the user (counts only) and plan prices — for honest offers
  const teasers = useTeasers();
  const plans = usePlans();
  const offer = useCallback((f: Feature) => {
    const need = caps.need(f);
    return { teaser: teasers[f], price: ((n) => (n == null ? undefined : fmtPrice(n)))(plans.find((p) => p.id === need)?.prices[0].price), trial: caps.trialAvailable };
  }, [caps, teasers, plans]);
  const explain = useCallback((f: Feature, mode: 'value' | 'example' = 'value') => lockedAnswer(f, caps.need(f), caps.planName, offer(f), mode), [caps, offer]);
  const gate = useCallback((a: Answer): Answer => {
    const f = a.requires?.find((x) => !caps.can(x as Feature)) as Feature | undefined;
    return f ? explain(f) : a;
  }, [caps, explain]);
  const allowed = useCallback((q: string) => !answers[q]?.requires?.some((x) => !caps.can(x as Feature)), [answers, caps]);
  // questions about plans and features are answered from the catalog — free, even at the limit
  const catalog = usePlanCatalog();
  const productQuestion = useCallback((q: string, ctx?: AskCtx) => {
    // «Что даёт Whale Alerts?» / «Покажи пример: Whale Alerts» on a plan without it
    for (const f of FEATURES) {
      if (caps.can(f)) continue;
      if (featureQuestion(f) === q) return explain(f);
      if (exampleQuestion(f) === q) return explain(f, 'example');
    }
    // a canned answer built on a locked feature becomes that feature's explanation — free as well
    const req = answers[q]?.requires?.find((x) => !caps.can(x as Feature)) as Feature | undefined;
    if (req) return explain(req);
    // on a locked preview: questions about that feature (what / why / is it worth it / an example)
    const lf = ctx?.lockedFeature as Feature | undefined;
    if (lf && (q === ctx!.question || /пример|зачем|стоит ли|что ещё|что еще|как работает|сколько стоит|чем полез|что даст|что получ|example|what for|why (do|would|should) i|is it worth|what else|how does it work|how much|what do i get|beispiel|wozu|warum (brauche|sollte)|lohnt|was noch|wie funktioniert|was kostet|was bringt/i.test(q) || q.toLowerCase().includes((FEATURE_INFO[lf]?.title ?? '#').toLowerCase())))
      return explain(lf, /пример|example|beispiel/i.test(q) ? 'example' : 'value');
    if (!isProductQuestion(q)) return undefined;
    const missed = FEATURES.filter((f) => !caps.can(f) && teasers[f]).map((f) => teasers[f]!);
    return productAnswer(q, catalog, caps.plan, caps.trialAvailable, missed);
  }, [catalog, caps, answers, explain, teasers]);
  /** a general market question («как дела на рынке?») gets the day's overview, not a demo stub */
  const general = useCallback((q: string) => (/рын(ок|ке|ка|ку)|что происходит|главн|как дела|обстанов|что нового|market|what'?s (new|happening)|today|markt|was ist los|heute|neu/i.test(q) ? Object.values(answers).find((a) => a.id === 'today') : undefined), [answers]);
  const limitReached = MOCK_DATA ? mock.ai === 'limit' || used >= limit : limit > 0 && used >= limit;

  // the server answers even if the message left the screen (bar closed, page changed)
  const finishRef = useRef<OrcaCtx['finish'] | null>(null);
  const chatOf = useRef(new Map<ItemId, string>());
  const settleLater = useCallback((it: { id: ItemId; fail: boolean }) => {
    window.setTimeout(() => {
      const chatId = chatOf.current.get(it.id);
      if (chatId && (inflight.current.has(it.id) || freeItems.current.has(it.id))) finishRef.current?.(chatId, it.id, it.fail ? 'error' : 'done');
    }, 20000);
  }, []);

  // a question about «my position» in a coin with no position → an honest answer, not invented numbers
  const qc = useQueryClient();
  const coinsRef = useRef(coins);
  coinsRef.current = coins;
  const positionGap = useCallback((q: string, ctx?: AskCtx) => {
    if (ctx?.kind === 'position' || !/позици|портфел|я купил|моих? (монет|актив)|position|portfolio|i bought|my (coins|assets)|gekauft|meine[nr]? (coins|assets)/i.test(q)) return null;
    const pf = qc.getQueryData<Portfolio>(['portfolio']);
    if (!pf) return null;
    const known = [...new Set([...coinsRef.current, ...pf.holdings.map((h) => h.symbol)])];
    // a coin named in the question wins over the one on screen
    const sym = known.find((c) => new RegExp(`\\b${c}\\b`).test(q)) ?? (ctx && /^[A-Z]{2,6}$/.test(ctx.tag) ? ctx.tag : undefined);
    if (!sym || pf.holdings.some((h) => h.symbol === sym)) return null;
    return noPositionAnswer(sym, coinsRef.current.includes(sym));
  }, [qc]);

  // ── ORCA API: one question → POST /ai/chat → the answer is polled until it is ready ──
  const stopped = useRef(new Set<ItemId>());
  const patchItem = useCallback((chatId: string, itemId: ItemId, fn: (x: ThreadItem) => ThreadItem) => {
    setChats((cs) => cs.map((c) => (c.id !== chatId ? c : { ...c, items: c.items.map((x) => (x.id === itemId ? fn(x) : x)) })));
  }, []);
  const pollReal = useCallback(async (chatId: string, itemId: ItemId, requestId: string) => {
    const settle = (state: ItemState, extra: Partial<ThreadItem> = {}) => {
      inflight.current.delete(itemId);
      patchItem(chatId, itemId, (x) => (x.state === 'stopped' ? x : { ...x, state, animate: false, ...extra }));
      void qc.invalidateQueries({ queryKey: ['ai-quota'] });
      void qc.invalidateQueries({ queryKey: ['ai-conversations'] });
    };
    const why = (status: string) => i18n.t(status === 'plan_required' ? 'ai:real.plan' : status === 'no_coins' ? 'ai:real.noCoins' : status === 'expired' ? 'ai:real.timeout' : 'ai:error.text');
    try {
      const deadline = Date.now() + 100_000;
      let misses = 0;
      while (Date.now() < deadline) {
        await new Promise((r) => setTimeout(r, 1200));
        if (stopped.current.has(itemId)) { inflight.current.delete(itemId); void qc.invalidateQueries({ queryKey: ['ai-quota'] }); return; }
        let r;
        try { r = await aiApi.result(requestId); misses = 0; } catch (e) {
          // a dropped poll is retried a few times; the answer waits on the server for 10 minutes
          if (++misses < 4 && !(e instanceof HttpError && e.status === 401)) continue;
          throw e;
        }
        if (r.quota) qc.setQueryData(['ai-quota'], r.quota);
        if (r.status === 'pending') continue;
        if (r.status === 'ok') { settle('done', { answer: { known: '', scenario: '', risk: '', html: r.reply_html ?? '' } }); return; }
        if (r.status === 'limit') { settle('limit'); return; }
        settle('error', { error: why(r.status) });
        return;
      }
      settle('error', { error: i18n.t('ai:real.timeout') });
    } catch (e) {
      const code = e instanceof HttpError ? e.code : '';
      if (code === 'quota_exhausted') { settle('limit'); return; }
      settle('error', { error: code === 'plan_required' ? why(code) : apiMessage(e) });
    }
  }, [patchItem, qc]);

  const runReal = useCallback(async (chatId: string, itemId: ItemId, message: string, content: string, ctx?: AskCtx) => {
    const settleStartError = (e: unknown) => {
      inflight.current.delete(itemId);
      const code = e instanceof HttpError ? e.code : '';
      patchItem(chatId, itemId, (x) => ({ ...x, state: code === 'quota_exhausted' ? 'limit' : 'error', animate: false, error: code === 'plan_required' ? i18n.t('ai:real.plan') : apiMessage(e) }));
      void qc.invalidateQueries({ queryKey: ['ai-quota'] });
    };
    try {
      const chat = chatsRef.current.find((c) => c.id === chatId);
      const current = chat?.items.find((x) => x.id === itemId);
      const messageId = current?.messageId ?? crypto.randomUUID();
      const started = await aiApi.ask({ message, content, messageId, conversationId: chat?.serverId, context: contextDescriptor(ctx) });
      setChats((cs) => cs.map((c) => c.id !== chatId ? c : ({
        ...c, serverId: started.conversation_id, at: nowIso(),
        items: c.items.map((x) => x.id === itemId ? { ...x, messageId: started.message_id, requestId: started.request_id } : x),
      })));
      void qc.invalidateQueries({ queryKey: ['ai-conversations'] });
      await pollReal(chatId, itemId, started.request_id);
    } catch (e) {
      settleStartError(e);
    }
  }, [patchItem, pollReal, qc]);

  useEffect(() => {
    if (MOCK_DATA) return;
    for (const chat of chats) for (const item of chat.items) {
      if (!item.real || item.state !== 'pending' || !item.requestId || inflight.current.has(item.id)) continue;
      inflight.current.add(item.id);
      chatOf.current.set(item.id, chat.id);
      void pollReal(chat.id, item.id, item.requestId);
    }
  }, [chats, pollReal]);

  const ask = useCallback<OrcaCtx['ask']>((q, o = {}) => {
    const m = getMock();
    const ctx = o.ctx ?? undefined;
    // the plan advisor is a real Orca AI question (its quota, its pipeline); the DEV mock has no model
    const product = o.answer || (ctx?.advisor && !MOCK_DATA) ? undefined : productQuestion(q, ctx);
    if (!MOCK_DATA && !product && !o.answer) {
      // a locked preview's context only explains its feature: any other question is a normal one
      const realCtx = ctx?.lockedFeature ? undefined : ctx;
      const blocked = limit > 0 && usedRef.current + inflight.current.size >= limit;
      // the object on screen goes with the question, as the user sees it in the chip
      const asked = realCtx && q !== realCtx.question ? `${realCtx.label}\n${q}` : q;
      const sent = realCtx?.brief ? `${realCtx.brief}\n\n${asked}` : asked;
      seq.current += 1;
      const item: ThreadItem = { id: seq.current, q, answer: { known: '', scenario: '', risk: '' }, ctx: realCtx, state: blocked ? 'limit' : 'pending',
        fail: false, attempt: 1, animate: !blocked, real: true, sent };
      const id = o.chatId ?? `c${Date.now()}${seq.current}`;
      setChats((cs) => (cs.some((c) => c.id === id)
        ? cs.map((c) => (c.id === id ? { ...c, items: [...c.items, item] } : c))
        : [{ id, title: q.replace(/[?«»]/g, '').slice(0, 64), tag: realCtx?.tag ?? i18n.t('ai:newTag'), at: nowIso(), items: [item] }, ...cs]));
      chatOf.current.set(item.id, id);
      if (!blocked) { inflight.current.add(item.id); void runReal(id, item.id, sent, q, realCtx); }
      return id;
    }
    const blocked = !product && (m.ai === 'limit' || usedRef.current + inflight.current.size >= limit);
    // a locked preview's context only explains its feature: any other question is a normal one
    const lockedCtx = !!ctx?.lockedFeature;
    const useCtx = lockedCtx && !product ? undefined : ctx;
    const known = o.answer ?? (q === useCtx?.question ? useCtx.answer : undefined) ?? answers[q] ?? general(q);
    // a free question with a context (e.g. coin focus) gets the context's answer
    const gap = o.answer ? null : positionGap(q, useCtx);
    const answer = product ?? gap ?? known ?? useCtx?.answer ?? fallback;
    seq.current += 1;
    const item: ThreadItem = {
      id: seq.current, q, answer, ctx: useCtx,
      state: blocked ? 'limit' : 'pending',
      fail: m.ai === 'error', attempt: 1, animate: !blocked, free: !!product,
    };
    if (!gap && !known && !useCtx && answer === fallback) item.answer = { ...fallback, lead: '__unknown__' };
    if (product) { freeItems.current.add(item.id); settleLater(item); }
    else if (!blocked) { inflight.current.add(item.id); settleLater(item); }
    const id = o.chatId ?? `c${Date.now()}${seq.current}`;
    setChats((cs) => (cs.some((c) => c.id === id)
      ? cs.map((c) => (c.id === id ? { ...c, items: [...c.items, item] } : c))
      : [{ id, title: q.replace(/[?«»]/g, '').slice(0, 64), tag: ctx?.tag ?? i18n.t('ai:newTag'), at: nowIso(), items: [item] }, ...cs]));
    chatOf.current.set(item.id, id);
    return id;
  }, [answers, fallback, limit, settleLater, positionGap, productQuestion, general, runReal]);

  const finish = useCallback<OrcaCtx['finish']>((chatId, itemId, state) => {
    setChats((cs) => cs.map((c) => (c.id !== chatId ? c : {
      ...c,
      items: c.items.map((x) => {
        if (x.id !== itemId) return x;
        const unknown = x.answer.lead === '__unknown__';
        return { ...x, state: state === 'done' && unknown ? 'unknown' : state, animate: false };
      }),
    })));
    // a successful answer costs one question; an error or a stop costs nothing
    // (counted once, even if the message and the server fallback both report it)
    freeItems.current.delete(itemId);
    // ORCA API: the server counts the quota; a stopped question is only no longer awaited here
    if (!MOCK_DATA) { if (state === 'stopped') stopped.current.add(itemId); return; }
    const counted = inflight.current.delete(itemId);
    if (state === 'done' && counted) void spendRef.current();
  }, []);
  finishRef.current = finish;
  const retry = useCallback<OrcaCtx['retry']>((chatId, itemId) => {
    const it = chats.find((c) => c.id === chatId)?.items.find((x) => x.id === itemId);
    if (it?.real) {
      stopped.current.delete(itemId);
      if (limit > 0 && usedRef.current + inflight.current.size >= limit) { patchItem(chatId, itemId, (x) => ({ ...x, state: 'limit', animate: false })); return; }
      inflight.current.add(itemId);
      patchItem(chatId, itemId, (x) => ({ ...x, state: 'pending', error: undefined, attempt: x.attempt + 1, animate: true }));
      void runReal(chatId, itemId, it.sent ?? it.q, it.q, it.ctx);
      return;
    }
    if (it?.free) {
      freeItems.current.add(itemId);
      settleLater({ id: itemId, fail: false });
      setChats((cs) => cs.map((c) => (c.id !== chatId ? c : { ...c, items: c.items.map((x) => (x.id === itemId ? { ...x, state: 'pending', fail: false, attempt: x.attempt + 1, animate: true } : x)) })));
      return;
    }
    if (getMock().ai === 'limit' || usedRef.current + inflight.current.size >= limit) {
      setChats((cs) => cs.map((c) => (c.id !== chatId ? c : { ...c, items: c.items.map((x) => (x.id === itemId ? { ...x, state: 'limit', animate: false } : x)) })));
      return;
    }
    inflight.current.add(itemId);
    settleLater({ id: itemId, fail: false });
    setChats((cs) => cs.map((c) => (c.id !== chatId ? c : { ...c, items: c.items.map((x) => (x.id === itemId ? { ...x, state: 'pending', fail: false, attempt: x.attempt + 1, animate: true } : x)) })));
  }, [limit, settleLater, chats, patchItem, runReal]);
  const removeChat = useCallback((chatId: string) => {
    const serverId = chatsRef.current.find((c) => c.id === chatId)?.serverId
      ?? (!MOCK_DATA && SERVER_CHAT_ID.test(chatId) ? chatId : undefined);
    setChats((cs) => cs.filter((c) => c.id !== chatId));
    if (!MOCK_DATA && serverId) void aiApi.removeConversation(serverId).then(() => {
      void qc.invalidateQueries({ queryKey: ['ai-conversations'] });
    }).catch(() => { void qc.invalidateQueries({ queryKey: ['ai-conversations'] }); });
  }, [qc]);
  const loadChat = useCallback(async (chatId: string) => {
    if (MOCK_DATA) return;
    const current = chatsRef.current.find((c) => c.id === chatId);
    if (current?.loaded) return;
    const detail = detailChat(await aiApi.conversation(current?.serverId ?? chatId));
    setChats((cs) => cs.some((c) => c.id === chatId)
      ? cs.map((c) => c.id === chatId ? { ...detail, id: chatId } : c)
      : [{ ...detail, id: chatId }, ...cs]);
  }, []);
  const searchChats = useCallback(async (query: string) => {
    if (MOCK_DATA) {
      const q = query.trim().toLowerCase();
      return chatsRef.current.filter((c) => !q || c.title.toLowerCase().includes(q) || c.items.some((x) => x.q.toLowerCase().includes(q)));
    }
    return (await aiApi.conversations(query)).map(summaryChat);
  }, []);

  const suggest = useCallback((base: string[], asked: string) =>
    [...new Set([...base, ...Object.keys(answers)])].filter((q) => q !== asked && allowed(q)).slice(0, 3), [answers, allowed]);

  // bar
  const askAbout = useCallback((ctx: AskCtx, question?: string) => {
    const chatId = ask(question ?? ctx.question, { ctx });
    setBar({ open: true, chatId, ctx });
  }, [ask, setBar]);
  // screen context registry: components on screen announce their object
  const [regs, setRegs] = useState<Array<{ id: string; ctx: AskCtx; priority: number; at: number }>>([]);
  const registerCtx = useCallback((id: string, ctx: AskCtx | null, priority: number) => {
    setRegs((rs) => {
      const rest = rs.filter((r) => r.id !== id);
      return ctx ? [...rest, { id, ctx, priority, at: Date.now() }] : rest;
    });
  }, []);
  const screenCtx = useMemo(() => [...regs].sort((a, b) => b.priority - a.priority || b.at - a.at)[0]?.ctx ?? null, [regs]);
  const askInBar = useCallback((q: string) => {
    const b = barRef.current;
    const chatId = ask(q, { chatId: b.chatId, ctx: b.ctx });
    setBar({ ...b, chatId });
  }, [ask, setBar]);
  const closeBar = useCallback(() => setBar({ ...barRef.current, open: false }), [setBar]);
  const clearBarCtx = useCallback(() => setBar({ ...barRef.current, ctx: null }), [setBar]);
  /** new page: the bar starts clean, the conversation stays in the history */
  const resetBar = useCallback(() => setBar({ open: false, chatId: null, ctx: null }), [setBar]);
  /** opening the bar without a conversation picks up what is on screen */
  const openBarWithScreen = useCallback(() => {
    const b = barRef.current;
    setBar({ ...b, open: true, ctx: b.chatId ? b.ctx : screenCtx });
  }, [setBar, screenCtx]);

  const value = useMemo<OrcaCtx>(() => ({
    used, limit, resetAt: resetLabel, limitReached, chats, ask, finish, retry, removeChat, loadChat, searchChats, suggest, gate, allowed,
    bar, askAbout, openBar: openBarWithScreen, closeBar, askInBar, clearBarCtx, resetBar, screenCtx, registerCtx,
  }), [used, limit, resetLabel, limitReached, chats, ask, finish, retry, removeChat, loadChat, searchChats, suggest, gate, allowed, bar, askAbout, openBarWithScreen, closeBar, askInBar, clearBarCtx, resetBar, screenCtx, registerCtx]);
  return <Ctx.Provider value={value}>{children}</Ctx.Provider>;
}
